import {
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import {
  suppliersApi,
  SupplierArticlesFilters,
} from '@texas/api/endpoints/suppliersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { Icons, defaultIconSize } from '@texas/components/shared/Icons';
import { ReactSelectOption } from '@texas/types';
import {
  saveBlobAsFile,
  excelExportFileName,
  exportPrefixes,
} from '@texas/utils/helpers/filesHelper';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { supplierArticlesColumns } from './Columns';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import {
  convertToEnum,
  getEnumNamesAndValues,
} from '@texas/utils/helpers/enumHelpers';
import { ArticleState } from '@texas/api/endpoints/articlesApi';
import { TexasHeader } from '@texas/components/shared/TexasHeader';

interface Props {
  supplierId: number;
}

const pageSize = 50;

const defaultFilters: SupplierArticlesFilters = {
  sortBy: 'itemCode',
  sortDesc: false,
  page: 1,
  pageSize: pageSize,
};

export function SupplierArticles({ supplierId }: Props) {
  const { t } = useTranslation();
  const [filters, setFilters] =
    useState<SupplierArticlesFilters>(defaultFilters);
  const [isExporting, setIsExporting] = useState(false);

  const { data, refetch, loading, error } = useApiResource(
    suppliersApi.getArticles,
  );

  const {
    branchesData: [branches, getBranches],
    productGroupsData: [productGroups, getProductGroups],
    customersData: [customers, getCustomers],
  } = useMetadata();

  useEffect(() => {
    getBranches();
    getProductGroups();
    getCustomers();
  }, [getBranches, getProductGroups, getCustomers]);

  const fetchArticles = useCallback(() => {
    refetch(supplierId, filters);
  }, [refetch, supplierId, filters]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const columns = useMemo(() => supplierArticlesColumns(t), [t]);

  const handleExport = async () => {
    if (!data || data.totalItems === 0) return;
    setIsExporting(true);
    try {
      const exportResponse = await suppliersApi.getArticles(supplierId, {
        ...filters,
        page: 1,
        pageSize: 0,
      });
      if (!exportResponse.hasError) {
        const allArticles = exportResponse.data.items;
        const response = await variantsApi.downloadExcel(
          allArticles.map((a) => ({
            variantId: a.variantId,
            branchId: a.branchId,
          })),
        );
        if (!response.hasError) {
          saveBlobAsFile(
            response.data,
            excelExportFileName(exportPrefixes.articles),
          );
        }
      }
    } finally {
      setIsExporting(false);
    }
  };

  const branchOptions = useMemo<ReactSelectOption[]>(
    () =>
      branches.map((b) => ({
        value: b.id,
        label: `${b.name} (${b.identifier})`,
      })),
    [branches],
  );

  const productGroupOptions = useMemo<ReactSelectOption[]>(
    () => productGroups.map((pg) => ({ value: pg.id, label: pg.value })),
    [productGroups],
  );

  const customerOptions = useMemo<ReactSelectOption[]>(
    () => customers.map((c) => ({ value: c.id, label: c.name })),
    [customers],
  );

  const yesNoOptions = useMemo<ReactSelectOption[]>(
    () => [
      { value: 1, label: t('general.yes') },
      { value: 0, label: t('general.no') },
    ],
    [t],
  );

  const stateOptions = useMemo<ReactSelectOption[]>(
    () =>
      getEnumNamesAndValues(ArticleState).map<ReactSelectOption>((x) => ({
        label: x.name,
        value: x.value,
      })),
    [],
  );

  return (
    <BodyOverride>
      <Card variant="dark">
        <CardBody>
          <Flex direction="column" gap={4}>
            <Flex justify="space-between" align="center">
              <TexasHeader text={t('general.articles')} />
              <Button
                variant="texas-light"
                leftIcon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
                onClick={handleExport}
                isDisabled={!data || data.totalItems === 0}
                isLoading={isExporting}
              >
                {t('general.exportExcel')}
              </Button>
            </Flex>
            <Flex gap={4} flexWrap="wrap" align="start">
              <FormControl maxW="300px">
                <FormLabel fontSize="sm">{t('general.branches')}</FormLabel>
                <TexasSelect
                  isClearable={true}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  placeholder={t('filter.allBranches')}
                  value={
                    filters.branchIds?.map<ReactSelectOption>((id) => {
                      const branch = branches.find((b) => b.id === id);
                      return {
                        label: branch
                          ? `${branch.name} (${branch.identifier})`
                          : '',
                        value: id,
                      };
                    }) ?? []
                  }
                  onChange={(selected) => {
                    const ids = selected.map((s) => s.value as number);
                    setFilters((f) => ({
                      ...f,
                      branchIds: ids.length > 0 ? ids : undefined,
                      page: 1,
                    }));
                  }}
                  options={branchOptions}
                />
              </FormControl>
              <FormControl maxW="300px">
                <FormLabel fontSize="sm">{t('general.productGroup')}</FormLabel>
                <TexasSelect
                  isClearable={true}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  placeholder={t('filter.allProductGroups')}
                  value={
                    filters.productGroupIds?.map<ReactSelectOption>((id) => {
                      const pg = productGroups.find((p) => p.id === id);
                      return { label: pg?.value ?? '', value: id };
                    }) ?? []
                  }
                  onChange={(selected) => {
                    const ids = selected.map((s) => s.value as number);
                    setFilters((f) => ({
                      ...f,
                      productGroupIds: ids.length > 0 ? ids : undefined,
                      page: 1,
                    }));
                  }}
                  options={productGroupOptions}
                />
              </FormControl>
              <FormControl maxW="300px">
                <FormLabel fontSize="sm">{t('general.brands')}</FormLabel>
                <TexasSelect
                  isClearable={true}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  placeholder={t('filter.allBrands')}
                  value={
                    filters.brandIds?.map<ReactSelectOption>((id) => {
                      const customer = customers.find((c) => c.id === id);
                      return { label: customer?.name ?? '', value: id };
                    }) ?? []
                  }
                  onChange={(selected) => {
                    const ids = selected.map((s) => s.value as number);
                    setFilters((f) => ({
                      ...f,
                      brandIds: ids.length > 0 ? ids : undefined,
                      page: 1,
                    }));
                  }}
                  options={customerOptions}
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">{t('general.state')}</FormLabel>
                <TexasSelect
                  isClearable={true}
                  placeholder={t('filter.all')}
                  value={
                    filters.garpState !== undefined
                      ? stateOptions.find((o) => o.value === filters.garpState)
                      : null
                  }
                  onChange={(selected) => {
                    setFilters((f) => ({
                      ...f,
                      garpState: selected
                        ? convertToEnum(ArticleState, selected.value)
                        : undefined,
                      page: 1,
                    }));
                  }}
                  options={stateOptions}
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">
                  {t('suppliers.articles.isMainSupplier')}
                </FormLabel>
                <TexasSelect
                  isClearable={true}
                  placeholder={t('filter.all')}
                  value={
                    filters.isMainPurchaseInfo !== undefined
                      ? yesNoOptions.find(
                          (o) =>
                            o.value === (filters.isMainPurchaseInfo ? 1 : 0),
                        ) ?? null
                      : null
                  }
                  onChange={(selected) => {
                    setFilters((f) => ({
                      ...f,
                      isMainPurchaseInfo: selected
                        ? selected.value === 1
                        : undefined,
                      page: 1,
                    }));
                  }}
                  options={yesNoOptions}
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">
                  {t('suppliers.articles.isMainVariant')}
                </FormLabel>
                <TexasSelect
                  isClearable={true}
                  placeholder={t('filter.all')}
                  value={
                    filters.isMainVariant !== undefined
                      ? yesNoOptions.find(
                          (o) => o.value === (filters.isMainVariant ? 1 : 0),
                        ) ?? null
                      : null
                  }
                  onChange={(selected) => {
                    setFilters((f) => ({
                      ...f,
                      isMainVariant: selected
                        ? selected.value === 1
                        : undefined,
                      page: 1,
                    }));
                  }}
                  options={yesNoOptions}
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">{t('garp.syncedToGarp')}</FormLabel>
                <TexasSelect
                  isClearable={true}
                  placeholder={t('filter.all')}
                  value={
                    filters.isSynced !== undefined
                      ? yesNoOptions.find(
                          (o) => o.value === (filters.isSynced ? 1 : 0),
                        ) ?? null
                      : null
                  }
                  onChange={(selected) => {
                    setFilters((f) => ({
                      ...f,
                      isSynced: selected ? selected.value === 1 : undefined,
                      page: 1,
                    }));
                  }}
                  options={yesNoOptions}
                />
              </FormControl>
            </Flex>

            <Text variant="sub" pt={2}>
              {t('suppliers.articles.foundVariants', {
                count: data?.totalItems ?? 0,
              })}
            </Text>

            <DataTableContainer
              w="100%"
              datatable={{
                data: data?.items ?? [],
                isLoading: loading,
                columns: columns,
                variant: 'configuration',
                sorted: {
                  key: filters.sortBy,
                  desc: filters.sortDesc,
                  onSortedChange: ({ key, desc }) => {
                    setFilters((f) => ({
                      ...f,
                      sortBy: key,
                      sortDesc: desc,
                      page: 1,
                    }));
                  },
                },
              }}
              pagination={{
                totalItems: data?.totalItems ?? 0,
                pageSize: filters.pageSize,
                currentPage: filters.page,
                onPageChange: (page) => setFilters((f) => ({ ...f, page })),
              }}
              error={error}
            />
          </Flex>
        </CardBody>
      </Card>
    </BodyOverride>
  );
}
